import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, videoCategories } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-10 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的视频不存在或已下架</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = videoCategories.find((c) => c.slug === video.category);
  const relatedVideos = videos.filter((v) => v.id !== video.id && v.category === video.category).slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    url: `https://www.lanmeishipin.com/article/${video.id}`,
    datePublished: video.releaseDate,
    author: video.director ? { '@type': 'Person', name: video.director } : undefined,
    inLanguage: 'zh-CN',
    image: video.thumbnail,
  };

  const videoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.description,
    thumbnailUrl: video.thumbnail,
    uploadDate: video.releaseDate,
    duration: video.duration,
    contentUrl: `https://www.lanmeishipin.com/article/${video.id}`,
    embedUrl: `https://www.lanmeishipin.com/embed/${video.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(videoJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1400px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: video.title },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <article className="flex-1">
                <div className="relative aspect-video rounded-xl overflow-hidden bg-background-950">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 蓝莓视频高清在线观看`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-white hover:bg-primary-500 hover:scale-110 transition-all cursor-pointer">
                      <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {video.isHot && <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">热门</span>}
                    {video.isNew && <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">新上</span>}
                    <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium">{video.quality}</span>
                    {video.episodes && <span className="px-2.5 py-1 bg-background-200 text-foreground-600 text-xs rounded-full font-medium">{video.episodes}</span>}
                  </div>

                  <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 mb-3">
                    {video.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <strong className="text-foreground-900">{video.rating}</strong> 分
                    </span>
                    <span>{video.views}次播放</span>
                    <span>{video.duration}</span>
                    <span>上映：{video.releaseDate}</span>
                  </div>

                  {video.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {video.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-lg">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="bg-background-100/70 rounded-xl p-5 md:p-6 mb-6">
                    <h2 className="text-base font-semibold text-foreground-900 mb-3">剧情简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {video.description}
                    </p>
                  </div>

                  {(video.director || video.actors.length > 0) && (
                    <div className="bg-background-100/70 rounded-xl p-5 md:p-6">
                      <h2 className="text-base font-semibold text-foreground-900 mb-3">演职人员</h2>
                      {video.director && (
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-sm text-foreground-500 w-16">导演：</span>
                          <span className="text-sm text-foreground-800">{video.director}</span>
                        </div>
                      )}
                      {video.actors.length > 0 && (
                        <div className="flex items-start gap-3">
                          <span className="text-sm text-foreground-500 w-16 whitespace-nowrap">主演：</span>
                          <div className="flex flex-wrap gap-2">
                            {video.actors.map((actor) => (
                              <span key={actor} className="px-3 py-1 bg-background-50 text-foreground-700 text-xs rounded-full border border-background-300">
                                {actor}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </article>

              {relatedVideos.length > 0 && (
                <aside className="lg:w-[320px] shrink-0">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    相关推荐
                  </h3>
                  <div className="flex flex-col gap-3">
                    {relatedVideos.map((rv) => (
                      <Link
                        key={rv.id}
                        to={`/article/${rv.id}`}
                        className="flex gap-3 bg-background-50 rounded-xl p-3 hover:bg-background-100 transition-colors cursor-pointer border border-background-200/70 group"
                      >
                        <div className="w-28 h-[72px] rounded-lg overflow-hidden shrink-0">
                          <img
                            src={rv.thumbnail}
                            alt={rv.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-1">
                            {rv.title}
                          </h4>
                          <p className="text-xs text-foreground-500 flex items-center gap-1">
                            <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                            {rv.rating}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </aside>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}